import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import BlockIcon from '@mui/icons-material/Block';
import { editUser, fetchPricingPlans } from '../../Action/api';
import { getAuthTokenPayload } from '../../Utils/index';
import appUIConfigs from '../../Components/Layout/Header/appUIConfigs';

const EditUserDialog = ({ open, onClose, onSuccess, userData }) => {
  const emptyAppAccess = () => Object.keys(appUIConfigs).reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});

  const [editedUser, setEditedUser] = useState({
    name: '',
    email: '',
    type: 'User',
    permissions: {
      dashboard: { read: false, write: false },
      runpost: { read: false, write: false },
      safepost: { read: false, write: false },
      approvals: { read: false, write: false },
      users: { read: false, write: false },
      settings: { read: false, write: false },
      marketingassets: { read: false, write: false },
      secureaccounts: { read: false, write: false },
      calendarschedule: { read: false, write: false }
    }
  });
  const [appAccess, setAppAccess] = useState(emptyAppAccess());
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectAllRead, setSelectAllRead] = useState(false);
  const [selectAllWrite, setSelectAllWrite] = useState(false);
  const [planMenuList, setPlanMenuList] = useState([]);
  const [planLoading, setPlanLoading] = useState(true);

  // The apps (and their permission/url) available on the logged-in account's own plan.
  // A sub-user can only be granted access to apps the account itself has access to.
  const ownApps = getAuthTokenPayload()?.userProduct?.apps || {};

  // Fetch plan data based on planId from JWT token
  useEffect(() => {
    const loadPlan = async () => {
      const planId = getAuthTokenPayload()?.planId;
      // alert(`Debug: Retrieved planId from JWT token: ${planId}`); // Debugging line
      if (!planId) { setPlanLoading(false); return; }
      const result = await fetchPricingPlans(planId);
      if (result.success) setPlanMenuList(result.data);
      setPlanLoading(false);
    };
    loadPlan();
  }, []);

  // Get the plan entry for a given menuId
  const getPlanMenu = (menuId) => planMenuList.find(m => m.menuId === menuId) || null;

  // Returns true if the plan marks this menu as permission: 'no'
  const isPlanLocked = (menuId) => {
    if (planLoading || planMenuList.length === 0) return false;
    const planMenu = getPlanMenu(menuId);
    return planMenu ? planMenu.permission === 'no' : false;
  };

  // Returns true if the account's own token doesn't have active, subscribed access to this app
  const isAppLocked = (appKey) => {
    const own = ownApps[appKey];
    return !(
      own && own.url &&
      (own.permission === 'read' || own.permission === 'write') &&
      own.appStatus === 'Active' &&
      own.subscription === true
    );
  };

  const handleAppAccessChange = (appKey) => {
    if (isAppLocked(appKey)) return;
    setAppAccess(prev => ({ ...prev, [appKey]: !prev[appKey] }));
  };

  const modulesList = useMemo(() => [
    {
      key: 'dashboard',
      label: 'Dashboard',
      menuId: 'MENU-001',
      readOnly: true, // Only has read access
      writeOnly: false,
      description: 'View only - no write access available'
    },
    {
      key: 'calendarschedule',
      label: 'Calendar Schedule',
      menuId: 'MENU-011',
      readOnly: false,
      writeOnly: false, // Has both read and write
      description: 'Full access - both read and write available'
    },
    {
      key: 'runpost',
      label: 'Run Post',
      menuId: 'MENU-002',
      readOnly: false,
      writeOnly: true, // Only has write access
      description: 'Action only - no separate read access'
    },
    {
      key: 'safepost',
      label: 'Safe Post',
      menuId: 'MENU-003',
      readOnly: false,
      writeOnly: true, // Only has write access
      description: 'Action only - no separate read access'
    },
    {
      key: 'approvals',
      label: 'Approvals',
      menuId: 'MENU-006',
      readOnly: false,
      writeOnly: false, // Has both read and write
      description: 'Full access - both read and write available'
    },
    {
      key: 'marketingassets',
      label: 'Marketing Assets',
      menuId: 'MENU-009',
      readOnly: false,
      writeOnly: false, // Has both read and write
      description: 'Full access - both read and write available'
    },
    {
      key: 'secureaccounts',
      label: 'Secure Accounts',
      menuId: 'MENU-010',
      readOnly: false,
      writeOnly: false, // Has both read and write
      description: 'Full access - both read and write available'
    },
    {
      key: 'users',
      label: 'Users',
      menuId: 'MENU-007',
      readOnly: false,
      writeOnly: false, // Has both read and write
      description: 'Full access - both read and write available'
    },
    {
      key: 'settings',
      label: 'Settings',
      menuId: 'MENU-008',
      readOnly: false,
      writeOnly: false, // Has both read and write
      description: 'Full access - both read and write available'
    }
  ], []);

  // Helper function to update localStorage if editing current user
  const updateCurrentUserPermissions = (menuList, userRole, userProduct) => {
    const currentUserId = localStorage.getItem('userId');
    
    // Only update if we're editing the currently logged-in user
    if (currentUserId && currentUserId === editedUser.email) {
      console.log('Updating localStorage for current user:', currentUserId);
      
      // Update menuList in localStorage
      localStorage.setItem('menuList', JSON.stringify(menuList));
      
      // Update roleName if changed
      localStorage.setItem('roleName', userRole);
      
      // Generate new userPermissions object
      const userPermissions = {};
      menuList.forEach((menu) => {
        userPermissions[menu.menuId] = {
          menuName: menu.menuName,
          permission: menu.permission,
          readAccess: menu.readAccess === "yes",
          writeAccess: menu.writeAccess === "yes",
          hasAccess: menu.readAccess === "yes" || menu.writeAccess === "yes",
        };
      });
      
      // Update userPermissions in localStorage
      localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
      
      // Update userData object
      try {
        const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}');
        const updatedUserData = {
          ...currentUserData,
          staffFullName: editedUser.name.trim(),
          roleName: userRole,
          menuList: menuList,
          userProduct: userProduct
        };
        localStorage.setItem('userData', JSON.stringify(updatedUserData));
        
        // Update userName in localStorage
        localStorage.setItem('userName', editedUser.name.trim());
        
        console.log('✅ LocalStorage updated successfully:', {
          menuList: menuList,
          userPermissions: userPermissions,
          roleName: userRole,
          userName: editedUser.name.trim()
        });
        
        // Optional: Trigger a storage event to notify other components
        window.dispatchEvent(new Event('storage'));
        
      } catch (error) {
        console.error('Error updating userData in localStorage:', error);
      }
    }
  };

  useEffect(() => {
    if (userData) {
      // Parse permissions from existing user data with proper handling of new permission structure
      const permissions = {};
      modulesList.forEach(module => {
        const menuItem = userData.menuList?.find(m => m.menuId === module.menuId);
        if (menuItem) {
          let readAccess = false;
          let writeAccess = false;

          if (module.readOnly) {
            // Dashboard: Only read access possible
            readAccess = menuItem.readAccess === "yes" || menuItem.permission === "read";
            writeAccess = false; // Never true for read-only modules
          } else if (module.writeOnly) {
            // Posts/Campaigns: Only write access possible
            readAccess = false; // Never true for write-only modules
            writeAccess = menuItem.writeAccess === "yes" || menuItem.permission === "write";
          } else {
            // Normal modules: Both read and write possible
            readAccess = menuItem.readAccess === "yes" || menuItem.permission === "read" || menuItem.permission === "write";
            writeAccess = menuItem.writeAccess === "yes" || menuItem.permission === "write";
          }

          permissions[module.key] = {
            read: readAccess,
            write: writeAccess
          };
        } else {
          permissions[module.key] = { read: false, write: false };
        }
      });

      setEditedUser({
        name: userData.staffFullName || '',
        email: userData.userId || '',
        type: userData.roleName || 'User',
        permissions: permissions
      });

      // Pre-fill app access toggles from the user's existing userProduct.apps (if any)
      const existingApps = userData.userProduct?.apps || {};
      const nextAppAccess = emptyAppAccess();
      Object.keys(nextAppAccess).forEach(appKey => {
        const existing = existingApps[appKey];
        nextAppAccess[appKey] = !!(existing && (existing.permission === 'read' || existing.permission === 'write'));
      });
      setAppAccess(nextAppAccess);

      // Update select all states based on available modules
      const availableReadModules = modulesList.filter(m => !m.writeOnly);
      const availableWriteModules = modulesList.filter(m => !m.readOnly);
      
      const allReadChecked = availableReadModules.every(m => permissions[m.key].read);
      const allWriteChecked = availableWriteModules.every(m => permissions[m.key].write);
      
      setSelectAllRead(allReadChecked);
      setSelectAllWrite(allWriteChecked);
    }
  }, [userData, modulesList]);

  const handlePermissionChange = (module, accessType) => {
    const moduleConfig = modulesList.find(m => m.key === module);
    // Do not allow changes for plan-locked modules
    if (isPlanLocked(moduleConfig.menuId)) return;
    
    setEditedUser(prev => {
      let updatedPermissions = { ...prev.permissions };
      
      if (moduleConfig.readOnly) {
        // Dashboard: Only read access available
        if (accessType === 'read') {
          updatedPermissions[module] = {
            read: !prev.permissions[module].read,
            write: false // Always false for read-only modules
          };
        }
        // Write checkbox should be disabled for read-only modules
      } else if (moduleConfig.writeOnly) {
        // Run Post, Safe Post, Email Campaign, SMS Campaign: Only write access
        if (accessType === 'write') {
          updatedPermissions[module] = {
            read: false, // Always false for write-only modules
            write: !prev.permissions[module].write
          };
        }
        // Read checkbox should be disabled for write-only modules
      } else {
        // Normal modules with both read and write (Approvals, Users, Settings)
        if (accessType === 'write') {
          // If checking write, also check read
          if (!prev.permissions[module][accessType]) {
            updatedPermissions[module] = {
              read: true,  // Auto-select read when write is selected
              write: true
            };
          } else {
            // If unchecking write, keep read as is
            updatedPermissions[module] = {
              ...prev.permissions[module],
              write: false
            };
          }
        } else if (accessType === 'read') {
          // If unchecking read and write is checked, uncheck write too
          if (prev.permissions[module].read && prev.permissions[module].write) {
            updatedPermissions[module] = {
              read: false,
              write: false  // Uncheck write if read is unchecked
            };
          } else {
            // Just toggle read
            updatedPermissions[module] = {
              ...prev.permissions[module],
              read: !prev.permissions[module].read
            };
          }
        }
      }

      // Update select all states
      const availableReadModules = modulesList.filter(m => !m.writeOnly);
      const availableWriteModules = modulesList.filter(m => !m.readOnly);
      
      const allReadChecked = availableReadModules.every(m => updatedPermissions[m.key].read);
      const allWriteChecked = availableWriteModules.every(m => updatedPermissions[m.key].write);
      
      setSelectAllRead(allReadChecked);
      setSelectAllWrite(allWriteChecked);

      return {
        ...prev,
        permissions: updatedPermissions
      };
    });
  };

  const handleSelectAllRead = () => {
    const newValue = !selectAllRead;
    setSelectAllRead(newValue);
    
    setEditedUser(prev => {
      const updatedPermissions = { ...prev.permissions };
      
      // Only update modules that support read access (not writeOnly) and are not plan-locked
      modulesList.forEach(module => {
        if (!module.writeOnly && !isPlanLocked(module.menuId)) {
          updatedPermissions[module.key] = {
            read: newValue,
            write: newValue ? prev.permissions[module.key].write : false // Uncheck write if read is unchecked
          };
        }
      });
      
      // Update select all write state
      const availableWriteModules = modulesList.filter(m => !m.readOnly);
      const allWriteChecked = availableWriteModules.every(m => updatedPermissions[m.key].write);
      setSelectAllWrite(allWriteChecked);
      
      return {
        ...prev,
        permissions: updatedPermissions
      };
    });
  };

  const handleSelectAllWrite = () => {
    const newValue = !selectAllWrite;
    setSelectAllWrite(newValue);
    
    setEditedUser(prev => {
      const updatedPermissions = { ...prev.permissions };
      
      // Only update modules that support write access (not readOnly) and are not plan-locked
      modulesList.forEach(module => {
        if (!module.readOnly && !isPlanLocked(module.menuId)) {
          if (module.writeOnly) {
            // Write-only modules don't need read access
            updatedPermissions[module.key] = {
              read: false,
              write: newValue
            };
          } else {
            // Normal modules need read access when write is selected
            updatedPermissions[module.key] = {
              read: newValue ? true : prev.permissions[module.key].read,
              write: newValue
            };
          }
        }
      });
      
      // If selecting all write, also select all read for applicable unlocked modules
      if (newValue) {
        const availableReadModules = modulesList.filter(m => !m.writeOnly && !isPlanLocked(m.menuId));
        availableReadModules.forEach(module => {
          if (updatedPermissions[module.key].write) {
            updatedPermissions[module.key].read = true;
          }
        });
        setSelectAllRead(true);
      }
      
      return {
        ...prev,
        permissions: updatedPermissions
      };
    });
  };

  const handleResetPermissions = () => {
    setEditedUser(prev => ({
      ...prev,
      permissions: {
        dashboard: { read: false, write: false },
        runpost: { read: false, write: false },
        safepost: { read: false, write: false },
        approvals: { read: false, write: false },
        users: { read: false, write: false },
        settings: { read: false, write: false },
        marketingassets: { read: false, write: false },
        secureaccounts: { read: false, write: false },
        calendarschedule: { read: false, write: false }
      }
    }));
    setAppAccess(emptyAppAccess());

    // Reset select all states
    setSelectAllRead(false);
    setSelectAllWrite(false);
  };

  const handleSubmit = async () => {
    // Name validation
    if (!editedUser.name.trim()) {
      setFormError('Full Name is required');
      return;
    }

    // Check if at least one permission is selected
    const hasAnyPermission = Object.values(editedUser.permissions).some(
      perm => perm.read || perm.write
    );

    if (!hasAnyPermission) {
      setFormError('Please select at least one module permission (Read or Write access)');
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    try {
      const menuList = modulesList.map(module => {
        const permissions = editedUser.permissions[module.key];
        let permission = "no";
        let readAccess = "no";
        let writeAccess = "no";
        
        if (module.readOnly) {
          // Dashboard: Only read access
          if (permissions.read) {
            permission = "read";
            readAccess = "yes";
            writeAccess = "no";
          }
        } else if (module.writeOnly) {
          // Run Post, Safe Post, Email Campaign, SMS Campaign: Only write access
          if (permissions.write) {
            permission = "write";
            readAccess = "no";
            writeAccess = "yes";
          }
        } else {
          // Normal modules: Both read and write available
          if (permissions.read && permissions.write) {
            permission = "write"; // Write access includes read
            readAccess = "yes";
            writeAccess = "yes";
          } else if (permissions.read) {
            permission = "read";
            readAccess = "yes";
            writeAccess = "no";
          } else if (permissions.write) {
            // This shouldn't happen in normal modules due to our logic
            permission = "write";
            readAccess = "yes";
            writeAccess = "yes";
          }
        }

        // Include extra plan-specific fields (userLimit, storage, runLater, etc.) if present
        const planMenu = getPlanMenu(module.menuId);
        const extraPlanFields = {};
        if (planMenu) {
          const knownBaseFields = ['menuId', 'menuName', 'permission', 'readAccess', 'writeAccess'];
          Object.keys(planMenu).forEach(field => {
            if (!knownBaseFields.includes(field)) {
              extraPlanFields[field] = planMenu[field];
            }
          });
        }

        return {
          menuId: module.menuId,
          menuName: module.label,
          permission: permission,
          readAccess: readAccess,
          writeAccess: writeAccess,
          ...extraPlanFields
        };
      });

      // Only grant apps the account itself has active, subscribed access to -
      // copy its full app entry (appId, appStatus, permission, subscription, url) as-is
      const apps = {};
      Object.keys(appAccess).forEach(appKey => {
        if (appAccess[appKey] && !isAppLocked(appKey)) {
          apps[appKey] = { ...ownApps[appKey] };
        }
      });

      const userProduct = {
        showAppIcon: Object.keys(apps).length > 0,
        apps: apps
      };

      const requestPayload = {
        userId: editedUser.email,
        staffFullName: editedUser.name.trim(),
        personalEmail: editedUser.email,
        roleName: editedUser.type,
        menuList: menuList,
        planId: getAuthTokenPayload()?.planId,
        userProduct: userProduct
      };

      console.log('Submitting user update:', requestPayload);

      const response = await editUser(requestPayload);

      if (response.data && response.data.status) {
        console.log('✅ User update successful');

        // Update localStorage if we're editing the current user
        updateCurrentUserPermissions(menuList, editedUser.type, userProduct);
        
        // Show success message
        onSuccess(editedUser.name);
        
        // Close dialog
        onClose();
        
        // Optional: Show a notification that localStorage was updated
        const currentUserId = localStorage.getItem('userId');
        if (currentUserId && currentUserId === editedUser.email) {
          // You can add a toast notification here if you want
          console.log('📝 Your permissions have been updated. Changes will take effect immediately.');
        }
        
      } else {
        setFormError(response.data?.message || 'Failed to update user.');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setFormError('An error occurred while updating the user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
        }
      }}
    >
      <Box sx={{ 
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        pt: 3, 
        pb: 4,
        px: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 120%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)',
        }} />
        <Typography variant="h5" sx={{ 
          color: 'white',
          fontWeight: 700,
          position: 'relative',
          mb: 1
        }}>
          Edit User
        </Typography>
        <Typography variant="body2" sx={{ 
          color: alpha('#fff', 0.7),
          position: 'relative'
        }}>
          Modify user details and permissions
          {/* Show indicator if editing current user */}
          {localStorage.getItem('userId') === editedUser.email && (
            <Typography component="span" sx={{ 
              display: 'block',
              mt: 0.5,
              color: '#fbbf24',
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              ⚠️ Note: You are editing your own account. Changes will apply immediately.
            </Typography>
          )}
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3, mt: 1 }}>
        {formError && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': { alignItems: 'center' }
            }}
          >
            {formError}
          </Alert>
        )}

        {/* Show info alert if editing current user */}
        {localStorage.getItem('userId') === editedUser.email && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': { alignItems: 'center' }
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Editing Your Own Account
              </Typography>
              <Typography variant="body2">
                You are modifying your own user account. Any permission changes will take effect immediately 
                without requiring you to log out and log back in.
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Info Alert explaining permission structure */}
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': { alignItems: 'center' }
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Permission Structure
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • <strong>Dashboard:</strong> View-only access (no write operations available)<br/>
              • <strong>Run Post / Safe Post:</strong> Action-only access (no separate read access)<br/>
              • <strong>Approvals/Users/Settings:</strong> Full read/write access available
            </Typography>
          </Box>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="name"
              label="Full Name"
              fullWidth
              required
              value={editedUser.name}
              onChange={(e) => setEditedUser(prev => ({ ...prev, name: e.target.value }))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9'
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="email"
              label="Email Address"
              fullWidth
              disabled
              value={editedUser.email}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f1f5f9',
                  '&.Mui-disabled': {
                    backgroundColor: '#f1f5f9'
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel shrink>User Type</InputLabel>
              <Select
                value={editedUser.type}
                label="User Type"
                disabled
                sx={{
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  '& .MuiSelect-select': { color: '#1e293b', WebkitTextFillColor: '#1e293b' },
                }}
              >
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </Select>
              <Typography variant="caption" sx={{ mt: 0.75, color: '#94a3b8', fontSize: '0.7rem' }}>
                Role cannot be changed after account creation.
              </Typography>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                backgroundColor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha('#3b82f6', 0.1)
                }}>
                  <LockPersonIcon sx={{ color: '#3b82f6' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#1e293b', mb: 0.5 }}>
                    Module Permissions
                    <Typography component="span" sx={{ color: '#dc2626', ml: 0.5, fontSize: '0.875rem' }}>
                      (Required)
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Configure access for each module - at least one permission required
                  </Typography>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e2e8f0' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                        Module
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectAllRead}
                              indeterminate={
                                modulesList.filter(m => !m.writeOnly && !isPlanLocked(m.menuId)).some(m => editedUser.permissions[m.key].read) &&
                                !modulesList.filter(m => !m.writeOnly && !isPlanLocked(m.menuId)).every(m => editedUser.permissions[m.key].read)
                              }
                              onChange={handleSelectAllRead}
                              sx={{
                                color: '#cbd5e1',
                                '&.Mui-checked': { color: '#10b981' },
                                '&.MuiCheckbox-indeterminate': { color: '#10b981' },
                              }}
                            />
                          }
                          label="Read"
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontWeight: 600,
                              color: '#1e293b'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectAllWrite}
                              indeterminate={
                                modulesList.filter(m => !m.readOnly && !isPlanLocked(m.menuId)).some(m => editedUser.permissions[m.key].write) &&
                                !modulesList.filter(m => !m.readOnly && !isPlanLocked(m.menuId)).every(m => editedUser.permissions[m.key].write)
                              }
                              onChange={handleSelectAllWrite}
                              sx={{
                                color: '#cbd5e1',
                                '&.Mui-checked': { color: '#f59e0b' },
                                '&.MuiCheckbox-indeterminate': { color: '#f59e0b' },
                              }}
                            />
                          }
                          label="Action"
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontWeight: 600,
                              color: '#1e293b'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleResetPermissions}
                          sx={{
                            minWidth: 'auto',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem',
                            color: '#dc2626',
                            borderColor: '#fca5a5',
                            backgroundColor: '#fef2f2',
                            '&:hover': {
                              borderColor: '#dc2626',
                              backgroundColor: '#fee2e2'
                            }
                          }}
                        >
                          Reset
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {planLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={20} sx={{ color: '#3b82f6' }} />
                          <Typography variant="caption" sx={{ ml: 1, color: '#64748b' }}>Loading plan permissions...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      modulesList.map((module) => {
                        const perm = editedUser.permissions[module.key] || { read: false, write: false };
                        const locked = isPlanLocked(module.menuId);
                        return (
                          <TableRow
                            key={module.key}
                            sx={{
                              '&:nth-of-type(odd)': { bgcolor: locked ? '#fff5f5' : '#fafbfc' },
                              ...(locked && { bgcolor: '#fff5f5 !important' })
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500, color: locked ? '#94a3b8' : '#475569' }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {locked && <BlockIcon sx={{ fontSize: 14, color: '#ef4444' }} />}
                                    <Typography variant="body2" sx={{
                                      fontWeight: 500,
                                      textDecoration: locked ? 'line-through' : 'none',
                                      color: locked ? '#94a3b8' : 'inherit'
                                    }}>
                                      {module.label}
                                    </Typography>
                                    {locked && (
                                      <Chip
                                        label="Not in plan"
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: '0.62rem',
                                          bgcolor: '#fef2f2',
                                          color: '#dc2626',
                                          border: '1px solid #fca5a5',
                                          '& .MuiChip-label': { px: 0.75 }
                                        }}
                                      />
                                    )}
                                  </Box>
                                  <Typography variant="caption" sx={{
                                    color: locked ? '#fca5a5' : module.readOnly ? '#059669' : module.writeOnly ? '#d97706' : '#64748b',
                                    fontStyle: 'italic'
                                  }}>
                                    {locked ? 'Upgrade your plan to access this module' : module.description}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip
                                title={locked ? 'Not available in your plan' : module.writeOnly ? 'Read access not available for this module' : ''}
                                placement="top"
                              >
                                <span>
                                  <Checkbox
                                    checked={!!perm.read}
                                    onChange={() => handlePermissionChange(module.key, 'read')}
                                    disabled={module.writeOnly || locked}
                                    sx={{
                                      color: (module.writeOnly || locked) ? '#e2e8f0' : '#cbd5e1',
                                      '&.Mui-checked': { color: '#10b981' },
                                      '&.Mui-disabled': { color: '#e2e8f0' }
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip
                                title={locked ? 'Not available in your plan' : module.readOnly ? 'Action access not available for this module' : ''}
                                placement="top"
                              >
                                <span>
                                  <Checkbox
                                    checked={!!perm.write}
                                    onChange={() => handlePermissionChange(module.key, 'write')}
                                    disabled={module.readOnly || locked}
                                    sx={{
                                      color: (module.readOnly || locked) ? '#e2e8f0' : '#cbd5e1',
                                      '&.Mui-checked': { color: '#f59e0b' },
                                      '&.Mui-disabled': { color: '#e2e8f0' }
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              {/* Empty cell to align with Reset button column */}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: Object.values(editedUser.permissions).some(perm => perm.read || perm.write) 
                  ? '#e0f2fe' 
                  : '#fef2f2', 
                borderRadius: 1, 
                border: Object.values(editedUser.permissions).some(perm => perm.read || perm.write) 
                  ? '1px solid #b3e5fc' 
                  : '1px solid #fca5a5'
              }}>
                <Typography variant="caption" sx={{ 
                  color: Object.values(editedUser.permissions).some(perm => perm.read || perm.write) 
                    ? '#0277bd' 
                    : '#dc2626', 
                  fontWeight: 500 
                }}>
                  {Object.values(editedUser.permissions).some(perm => perm.read || perm.write) ? (
                    <>💡 <strong>Permission Guide:</strong> Different modules have different access patterns. Dashboard is view-only, Run Post/Safe Post are action-only, while Users/Settings/Approvals have both read and action access.</>
                  ) : (
                    <>⚠️ <strong>Required:</strong> Please select at least one module permission. Users must have access to at least one module to use the system.</>
                  )}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#1e293b', mb: 0.5 }}>
                  App Access
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Grant this user access to the OruClicks apps (billing, lead, contacts, recruit, etc.) available on your account
                </Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e2e8f0' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>App</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: '#1e293b' }}>Access</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(appUIConfigs).map((appKey) => {
                      const app = appUIConfigs[appKey];
                      const locked = isAppLocked(appKey);
                      return (
                        <TableRow
                          key={appKey}
                          sx={{
                            '&:nth-of-type(odd)': { bgcolor: locked ? '#fff5f5' : '#fafbfc' },
                            ...(locked && { bgcolor: '#fff5f5 !important' })
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500, color: locked ? '#94a3b8' : '#475569' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {locked && <BlockIcon sx={{ fontSize: 14, color: '#ef4444' }} />}
                              <Typography variant="body2" sx={{
                                fontWeight: 500,
                                textDecoration: locked ? 'line-through' : 'none',
                                color: locked ? '#94a3b8' : 'inherit'
                              }}>
                                {app.label}
                              </Typography>
                              {locked && (
                                <Chip
                                  label="Not on your account"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.62rem',
                                    bgcolor: '#fef2f2',
                                    color: '#dc2626',
                                    border: '1px solid #fca5a5',
                                    '& .MuiChip-label': { px: 0.75 }
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: locked ? '#fca5a5' : '#64748b', fontStyle: 'italic' }}>
                              {locked ? 'Not available - your account does not have this app' : app.description}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={locked ? 'Not available on your account' : ''} placement="top">
                              <span>
                                <Checkbox
                                  checked={!!appAccess[appKey]}
                                  onChange={() => handleAppAccessChange(appKey)}
                                  disabled={locked}
                                  sx={{
                                    color: locked ? '#e2e8f0' : '#cbd5e1',
                                    '&.Mui-checked': { color: '#3b82f6' },
                                    '&.Mui-disabled': { color: '#e2e8f0' }
                                  }}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 3, 
          pt: 2,
          borderTop: '1px solid #e2e8f0',
          gap: 1
        }}
      >
        <Button 
          onClick={onClose}
          variant="outlined"
          disabled={isSubmitting}
          sx={{ 
            color: '#64748b',
            borderColor: '#e2e8f0',
            '&:hover': {
              borderColor: '#cbd5e1',
              backgroundColor: '#f8fafc'
            }
          }}
        >
          Cancel
        </Button>
        <Tooltip 
          title={!Object.values(editedUser.permissions).some(perm => perm.read || perm.write) 
            ? "Please select at least one module permission" 
            : ""
          }
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || !Object.values(editedUser.permissions).some(perm => perm.read || perm.write)}
              sx={{
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' },
                '&:disabled': {
                  bgcolor: '#cbd5e1',
                  color: '#94a3b8'
                },
                px: 3,
                py: 1
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  <span>Updating...</span>
                </Box>
              ) : (
                'Update User'
              )}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;